// owner: metrics-domain agent
import { z } from 'zod';
import { EventSourceSchema, UlidSchema } from './common';

export const MetricsPersonaSchema = z.enum([
  'seeker',
  'employee',
  'employer',
  'advertiser',
  'marketer',
  'creator',
  'agent_owner',
]);
export type MetricsPersona = z.infer<typeof MetricsPersonaSchema>;

const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const MetricsQuerySchema = z.object({
  from: Day.optional(),
  to: Day.optional(),
  /** org scope for employer/advertiser/marketer/creator views */
  org: UlidSchema.optional(),
  /** narrow to one owned campaign/job/link/post/token */
  entity: z.string().min(1).max(128).optional(),
});
export const MetricsPersonaParamsSchema = z.object({ persona: MetricsPersonaSchema });

export const SeriesPointSchema = z.object({ day: z.string(), value: z.number() });
export const MetricsResponseSchema = z.object({
  persona: MetricsPersonaSchema,
  from: z.string(),
  to: z.string(),
  days: z.array(z.string()),
  /** zero-filled day series keyed by metric name */
  series: z.record(z.string(), z.array(SeriesPointSchema)),
  /** raw totals over the range */
  totals: z.record(z.string(), z.number()),
  /** derived formulas; null when the denominator is 0 */
  summary: z.record(z.string(), z.number().nullable()),
  /** page views etc. split by traffic source (human/agent/ai_bot/search_bot) */
  sourceSplit: z.record(EventSourceSchema, z.number()),
  updatedAt: z.number().int().nullable(),
});
export type MetricsResponse = z.infer<typeof MetricsResponseSchema>;

export const SeoGeoQuerySchema = z.object({
  from: Day.optional(),
  to: Day.optional(),
  org: UlidSchema.optional(),
});
export const SeoGeoResponseSchema = z.object({
  from: z.string(),
  to: z.string(),
  days: z.array(z.string()),
  series: z.object({
    humanViews: z.array(SeriesPointSchema),
    searchBotFetches: z.array(SeriesPointSchema),
    aiBotFetches: z.array(SeriesPointSchema),
    agentViews: z.array(SeriesPointSchema),
  }),
  totals: z.object({
    humanViews: z.number(),
    searchBotFetches: z.number(),
    aiBotFetches: z.number(),
    agentViews: z.number(),
  }),
  referrals: z.array(z.object({ referrerKind: z.string(), count: z.number() })),
  byEntity: z.array(
    z.object({
      entityKind: z.string(),
      entityId: z.string(),
      humanViews: z.number(),
      searchBotFetches: z.number(),
      aiBotFetches: z.number(),
      agentViews: z.number(),
      seoEnabled: z.boolean().nullable(),
      geoEnabled: z.boolean().nullable(),
    }),
  ),
  notes: z.array(z.string()),
});
export type SeoGeoResponse = z.infer<typeof SeoGeoResponseSchema>;
